import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import api from '../services/api';

interface RegisterData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  phone: string;
  role: 'user' | 'partner';
  businessName?: string;
  truckData?: {
    name: string;
    typeId: number;
    description?: string;
    hasDelivery: boolean;
    deliveryRadius?: number;
    deliveryFeeCents?: number;
  };
}

interface TruckType {
  id: number;
  name: string;
  description?: string;
}

const Register: React.FC = () => {
  const [registerData, setRegisterData] = useState<RegisterData>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'user',
    businessName: '',
    truckData: {
      name: '',
      typeId: 0,
      description: '',
      hasDelivery: false,
      deliveryRadius: 10,
      deliveryFeeCents: 500,
    },
  });

  const [loading, setLoading] = useState(false);
  const [truckTypes, setTruckTypes] = useState<TruckType[]>([]);
  const [showTruckForm, setShowTruckForm] = useState(false);
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();

  // Fetch truck types when component mounts
  React.useEffect(() => {
    const fetchTruckTypes = async () => {
      try {
        const response = await api.get('/truck-types');
        setTruckTypes(response.data.data || []);
      } catch (error) {
        console.error('Failed to fetch truck types:', error);
      }
    };

    fetchTruckTypes();
  }, []);

  const handleInputChange = (field: keyof RegisterData, value: any) => {
    setRegisterData(prev => ({ ...prev, [field]: value }));
  };

  const handleTruckDataChange = (field: keyof NonNullable<RegisterData['truckData']>, value: any) => {
    setRegisterData(prev => ({
      ...prev,
      truckData: { ...prev.truckData!, [field]: value }
    }));
  };

  const validateForm = (): string | null => {
    if (!registerData.email || !registerData.password || !registerData.firstName || !registerData.lastName) {
      return 'Please fill in all required fields';
    }

    if (registerData.password !== registerData.confirmPassword) {
      return 'Passwords do not match';
    }

    if (registerData.password.length < 8) {
      return 'Password must be at least 8 characters long';
    }

    if (registerData.role === 'partner') {
      if (!registerData.businessName?.trim()) {
        return 'Business name is required for partners';
      }

      if (showTruckForm && (!registerData.truckData?.name || !registerData.truckData?.typeId)) {
        return 'Truck name and type are required';
      }
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      showError('Validation Error', validationError);
      return;
    }

    setLoading(true);
    
    try {
      const payload = {
        email: registerData.email,
        password: registerData.password,
        firstName: registerData.firstName,
        lastName: registerData.lastName,
        phone: registerData.phone || undefined,
        role: registerData.role,
        businessName: registerData.role === 'partner' ? registerData.businessName : undefined,
        truckData: registerData.role === 'partner' && showTruckForm ? registerData.truckData : undefined,
      };

      console.log('Registering with payload:', payload);
      
      const response = await api.post('/auth/register', payload);
      console.log('Registration successful:', response.data);
      
      showSuccess('Registration Successful', 'Account created successfully! Please login.');
      
      // Redirect to login page
      setTimeout(() => {
        navigate('/login');
      }, 1500);
      
    } catch (err: any) {
      console.error('Registration error:', err);
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          err.message || 
                          'Registration failed';
      showError('Registration Failed', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      <div className="hidden md:flex flex-col gap-6 p-10 border-r border-gray-200 dark:border-gray-800 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald-600 text-white">🚗</span>
          <span>TruckTrack</span>
        </div>
        <div className="mt-auto">
          <blockquote className="text-sm leading-6 text-gray-600 dark:text-gray-400">
            "Join our platform and start managing your truck services efficiently. Connect with customers and grow your business." – TruckTrack Team
          </blockquote>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <div className="mb-6 space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">Create Account</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Join TruckTrack and start managing your services
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">First Name *</label>
                <input
                  type="text"
                  value={registerData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className="w-full mt-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">Last Name *</label>
                <input
                  type="text"
                  value={registerData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className="w-full mt-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Email *</label>
              <input
                type="email"
                value={registerData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full mt-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium">Phone</label>
              <input
                type="tel"
                value={registerData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="w-full mt-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                placeholder="+1234567890"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Password *</label>
              <input
                type="password"
                value={registerData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className="w-full mt-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                minLength={8}
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium">Confirm Password *</label>
              <input
                type="password"
                value={registerData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                className="w-full mt-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                minLength={8}
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium">Account Type *</label>
              <select
                value={registerData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                className="w-full mt-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                required
              >
                <option value="user">Customer</option>
                <option value="partner">Partner (Truck Owner)</option>
              </select>
            </div>

            {/* Partner-specific fields */}
            {registerData.role === 'partner' && (
              <>
                <div>
                  <label className="text-sm font-medium">Business Name *</label>
                  <input
                    type="text"
                    value={registerData.businessName}
                    onChange={(e) => handleInputChange('businessName', e.target.value)}
                    className="w-full mt-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                    required
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="showTruckForm"
                    checked={showTruckForm}
                    onChange={(e) => setShowTruckForm(e.target.checked)}
                    className="rounded border-gray-300 dark:border-gray-700"
                  />
                  <label htmlFor="showTruckForm" className="text-sm">
                    Add truck information now
                  </label>
                </div>

                {showTruckForm && (
                  <div className="space-y-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800">
                    <h3 className="font-medium">Truck Information</h3>
                    
                    <div>
                      <label className="text-sm font-medium">Truck Name *</label>
                      <input
                        type="text"
                        value={registerData.truckData?.name}
                        onChange={(e) => handleTruckDataChange('name', e.target.value)}
                        className="w-full mt-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                        required
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium">Truck Type *</label>
                      <select
                        value={registerData.truckData?.typeId}
                        onChange={(e) => handleTruckDataChange('typeId', parseInt(e.target.value))}
                        className="w-full mt-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                        required
                      >
                        <option value={0}>Select truck type</option>
                        {truckTypes.map(type => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="text-sm font-medium">Description</label>
                      <textarea
                        value={registerData.truckData?.description}
                        onChange={(e) => handleTruckDataChange('description', e.target.value)}
                        className="w-full mt-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                        rows={3}
                        placeholder="Describe your truck and services..."
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="hasDelivery"
                        checked={registerData.truckData?.hasDelivery}
                        onChange={(e) => handleTruckDataChange('hasDelivery', e.target.checked)}
                        className="rounded border-gray-300 dark:border-gray-700"
                      />
                      <label htmlFor="hasDelivery" className="text-sm">
                        Offer delivery service
                      </label>
                    </div>

                    {registerData.truckData?.hasDelivery && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium">Delivery Radius (km)</label>
                          <input
                            type="number"
                            value={registerData.truckData?.deliveryRadius}
                            onChange={(e) => handleTruckDataChange('deliveryRadius', parseInt(e.target.value))}
                            className="w-full mt-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                            min="1"
                            max="100"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Delivery Fee (cents)</label>
                          <input
                            type="number"
                            value={registerData.truckData?.deliveryFeeCents}
                            onChange={(e) => handleTruckDataChange('deliveryFeeCents', parseInt(e.target.value))}
                            className="w-full mt-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                            min="0"
                            step="100"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-medium py-2 px-4 rounded-md transition-colors"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>

            <div className="text-center text-sm text-gray-600 dark:text-gray-400">
              Already have an account?{' '}
              <Link to="/login" className="text-emerald-600 hover:text-emerald-700 font-medium">
                Sign in
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
